import axios from 'axios'
import config from '@/config'
import { useUserStore } from '@/stores/user'
import router from '@/router'
import { getCurrentNode, reportSuccess, reportFailure } from '@/utils/serverSelector'

// 创建 axios 实例
const request = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Token刷新状态
let isRefreshing = false
let failedQueue = []

// 处理等待队列
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  
  isRefreshing = false
  failedQueue = []
}

// 请求拦截器
request.interceptors.request.use(
  (reqConfig) => {
    // 每次请求前同步当前激活节点地址（切换节点后自动生效）
    reqConfig.baseURL = getCurrentNode().api
    const userStore = useUserStore()
    // 登录和注册接口不需要认证头
    const noAuthUrls = ['/api/user/login', '/api/game/login', '/api/user/register', '/api/game/register', '/api/user/captcha', '/api/game/captcha', '/api/config', '/api/user/refresh']
    const requestUrl = reqConfig.url || ''
    const needsAuth = !noAuthUrls.some(url => requestUrl.includes(url))
    
    if (needsAuth) {
      // 优先使用 store 中的 token；若短暂为空则回退 localStorage，避免请求丢失 Authorization 头
      let token = userStore.accessToken
      if (!token) {
        token = localStorage.getItem(config.storage.token) || ''
        if (token && userStore.setAccessToken) {
          userStore.setAccessToken(token)
        }
      }
      
      if (token) {
        reqConfig.headers.Authorization = `Bearer ${token}`
      }
    }
    
    // 添加会话ID（用于验证码）
    const sessionId = sessionStorage.getItem('session_id') || localStorage.getItem('session_id')
    if (sessionId) {
      reqConfig.headers['X-Session-Id'] = sessionId
    }
    return reqConfig
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  (response) => {
    const res = response.data
    if (res.code === 200) {
      return res
    } else if (res.code === 401) {
      // 未授权，尝试刷新token
      const userStore = useUserStore()
      const originalRequest = response.config
      
      if (!isRefreshing) {
        isRefreshing = true
        
        // 尝试用RefreshToken刷新
        return request.post('/api/user/refresh', {
          refreshToken: userStore.refreshToken
        })
          .then(res => {
            if (res.code === 200 && res.data.accessToken) {
              // 保存新的accessToken
              userStore.setAccessToken(res.data.accessToken)
              
              // 更新原请求的Authorization头
              originalRequest.headers.Authorization = `Bearer ${res.data.accessToken}`
              
              // 处理队列中的请求
              processQueue(null, res.data.accessToken)
              
              // 重新执行原请求
              return request(originalRequest)
            } else {
              throw new Error('刷新失败')
            }
          })
          .catch(error => {
            // 刷新失败，清除token并跳转登陆页
            processQueue(error, null)
            userStore.logout()
            router.push('/login')
            return Promise.reject(new Error(res.message || '未授权'))
          })
      } else {
        // 已经在刷新，加入队列
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return request(originalRequest)
          })
          .catch(err => {
            return Promise.reject(err)
          })
      }
    } else {
      return Promise.reject(new Error(res.message || '请求失败'))
    }
  },
  async (error) => {
    console.error('请求错误:', error)
    // 网络层错误（超时 / 断电 / 连接拒绝）且未重试过 → 触发切换并重试一次
    const isNetworkErr = !error.response && (
      error.code === 'ECONNABORTED' ||
      error.code === 'ERR_NETWORK'  ||
      error.message?.includes('timeout') ||
      error.message?.includes('Network Error')
    )
    const orig = error.config
    if (isNetworkErr && orig && !orig._retried) {
      orig._retried = true
      await reportFailure()               // 计数+达阈値后切换节点
      orig.baseURL = getCurrentNode().api // 用新节点重试
      try { return await request(orig) } catch (e) { return Promise.reject(e) }
    }
    if (error.response) {
      if (error.response.status === 401) {
        const userStore = useUserStore()
        userStore.logout()
        router.push('/login')
      }
      return Promise.reject(new Error(error.response.data?.message || '请求失败'))
    }
    return Promise.reject(error)
  }
)

// 响应成功时重置失败计数
request.interceptors.response.use(
  (response) => { reportSuccess(); return response },
  (err) => Promise.reject(err)
)

export default request

